/**
 * 일요일 등원 상점 설정
 * - 자동 부여 ON/OFF
 * - 시간당 점수
 * - 일 한도 (그 일요일 하루 최대 점수)
 * - 학부모 알림톡 발송 여부
 */
import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../../../infrastructure/api';

interface Settings {
  organization_id: string;
  enabled: boolean;
  points_per_hour: number;
  max_per_day: number;
  notify_parent: boolean;
}

const SundayMeritSettings: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/v1/owner/sunday-merit/settings');
      setSettings(res.data);
    } catch (e) {
      console.error('일요일 상점 설정 로드 실패:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (patch: Partial<Settings>) => {
    if (!settings) return;
    setSaving(true);
    try {
      const res = await apiClient.put('/v1/owner/sunday-merit/settings', patch);
      setSettings(res.data);
    } catch (e: any) {
      alert(e?.response?.data?.detail || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="bg-white rounded-lg border p-4 text-sm text-gray-500">
        일요일 상점 설정 로딩 중...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            🌟 일요일 등원 상점 (자동 부여)
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            매주 월요일 아침 08:00에 전날(일요일) 출석 학생에게 자동으로 상점 부여 + 학부모 알림
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.enabled}
            disabled={saving}
            onChange={(e) => update({ enabled: e.target.checked })}
            className="w-5 h-5"
          />
          <span className={`text-sm font-medium ${settings.enabled ? 'text-green-700' : 'text-gray-500'}`}>
            {settings.enabled ? '활성' : '비활성'}
          </span>
        </label>
      </div>

      <div className={`p-4 space-y-3 ${!settings.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">시간당 점수</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={10}
                value={settings.points_per_hour}
                disabled={saving}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                  setSettings({ ...settings, points_per_hour: v });
                }}
                onBlur={() => update({ points_per_hour: settings.points_per_hour })}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
              />
              <span className="text-sm text-gray-500">점 / 시간</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">일 한도 (그 일요일 하루 최대)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={100}
                value={settings.max_per_day}
                disabled={saving}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(100, parseInt(e.target.value) || 1));
                  setSettings({ ...settings, max_per_day: v });
                }}
                onBlur={() => update({ max_per_day: settings.max_per_day })}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
              />
              <span className="text-sm text-gray-500">점</span>
            </div>
          </div>
        </div>

        <div className="border-t pt-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notify_parent}
              disabled={saving}
              onChange={(e) => update({ notify_parent: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-sm">📨 부여 시 학부모에게 알림톡 발송</span>
          </label>
          <p className="text-xs text-gray-400 ml-6 mt-1">
            (NHN Cloud에 'sunday_merit_alert' 템플릿 등록 필요)
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800">
          💡 <b>예시</b>: 일요일에 5시간 등원 → {settings.points_per_hour * 5}점 (하루 한도 {settings.max_per_day}점 이내)
          <br />
          • 12시간 등원해도 한도({settings.max_per_day}점)까지만 부여됩니다.
          <br />
          • 다음 일요일은 다시 0부터 시작합니다.
        </div>
      </div>
    </div>
  );
};

export default SundayMeritSettings;
